import { Reply, Trash2, Play, Pause, FileText, Download } from 'lucide-react'
import { memo, useState, useRef, useEffect } from 'react'
import { clsx } from 'clsx'
import { ReactionBar } from './ReactionBar'
import { ChatImageMessage } from './ChatImageMessage'
import { ChatTaskCard } from './ChatTaskCard'
import { ChatMemoCard } from './ChatMemoCard'
import { ChatReplyPreview } from './ChatReplyPreview'
import type { ChatMessage, SharedGroupMember } from '@/lib/sharedTypes'

interface ChatMessageBubbleProps {
  message: ChatMessage
  isOwn: boolean
  isGrouped?: boolean
  showTime?: boolean
  groupId?: string
  // 메모 효율을 위해 콜백은 메시지 id를 받는 안정적인(stable) 핸들러를 받는다.
  onReply: (messageId: string) => void
  onReactionAdd: (messageId: string, emoji: string) => void
  onReactionRemove: (messageId: string, emoji: string) => void
  onDelete: (messageId: string) => void
  onLongPress?: (messageId: string) => void
  onToggleTask?: (taskId: string, currentStatus: 'pending' | 'completed') => void
  onScrollToMessage?: (messageId: string) => void
  unreadCount?: number
  members: SharedGroupMember[]
}

const URL_REGEX = /https?:\/\/[^\s<>"')\]},]+/g

function renderTextWithLinks(text: string, mentions?: string[], members?: SharedGroupMember[]) {
  // Step 1: 멘션 + URL을 모두 감지하여 React 엘리먼트로 변환
  const mentionMap = new Map<string, string>()
  if (mentions?.length && members?.length) {
    for (const uid of mentions) {
      const member = members.find((m) => m.uid === uid)
      if (member) mentionMap.set(`@${member.displayName}`, uid)
    }
  }

  // 모든 매칭 위치 수집 (멘션 + URL)
  type Match = { start: number; end: number; type: 'mention' | 'url'; value: string }
  const matches: Match[] = []

  // URL 매칭
  let urlMatch: RegExpExecArray | null
  const urlRegex = new RegExp(URL_REGEX.source, 'g')
  while ((urlMatch = urlRegex.exec(text)) !== null) {
    matches.push({ start: urlMatch.index, end: urlMatch.index + urlMatch[0].length, type: 'url', value: urlMatch[0] })
  }

  // 멘션 매칭 (같은 멘션이 여러 번 나와도 모두 하이라이트)
  for (const mention of mentionMap.keys()) {
    let searchFrom = 0
    while (true) {
      const idx = text.indexOf(mention, searchFrom)
      if (idx === -1) break
      matches.push({ start: idx, end: idx + mention.length, type: 'mention', value: mention })
      searchFrom = idx + mention.length
    }
  }

  if (matches.length === 0) return text

  // 위치순 정렬
  matches.sort((a, b) => a.start - b.start)

  const parts: (string | React.ReactElement)[] = []
  let cursor = 0
  let keyIdx = 0

  for (const m of matches) {
    if (m.start < cursor) continue // 겹치는 매치 건너뜀
    if (m.start > cursor) parts.push(text.slice(cursor, m.start))
    if (m.type === 'mention') {
      parts.push(<span key={keyIdx++} className="text-primary-500 font-medium">{m.value}</span>)
    } else {
      parts.push(
        <a key={keyIdx++} href={m.value} target="_blank" rel="noopener noreferrer" className="underline break-all">
          {m.value}
        </a>
      )
    }
    cursor = m.end
  }
  if (cursor < text.length) parts.push(text.slice(cursor))

  return <>{parts}</>
}

function formatTime(isoString: string): string {
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return ''
  const h = d.getHours()
  const m = d.getMinutes().toString().padStart(2, '0')
  const period = h < 12 ? '오전' : '오후'
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${period} ${hour12}:${m}`
}

function ChatMessageBubbleImpl({
  message,
  isOwn,
  isGrouped = false,
  showTime = true,
  groupId,
  onReply,
  onReactionAdd,
  onReactionRemove,
  onDelete,
  onLongPress,
  onToggleTask,
  onScrollToMessage,
  unreadCount,
  members,
}: ChatMessageBubbleProps) {
  const messageType = message.type || 'text'
  const reactions = message.reactions || {}
  const hasReactions = Object.values(reactions).some((users) => users.length > 0)

  // 이모지 전용 메시지 감지 (카카오톡 스타일 — 이모지만 있으면 크게 표시)
  const emojiOnlyRegex = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\p{Emoji_Component}\uFE0F\u200D\s]+$/u
  const trimmedText = message.text?.trim() || ''
  const isEmojiOnly = messageType === 'text' && trimmedText.length > 0 && emojiOnlyRegex.test(trimmedText)
  const emojiCount = isEmojiOnly ? [...trimmedText.replace(/\s/g, '')].length : 0

  // Hooks must be before any conditional returns (React rules)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 삭제된 메시지
  if (message.isDeleted) {
    return (
      <div className={clsx('flex items-end gap-2', isOwn && 'flex-row-reverse')}>
        {!isOwn && !isGrouped && <div className="w-7 h-7 flex-shrink-0" />}
        <div className="max-w-[75%]">
          <div
            className={clsx('rounded-xl px-3 py-2 opacity-60', isOwn ? 'rounded-br-sm' : 'rounded-bl-sm')}
            style={{
              background: isOwn
                ? 'var(--chat-bubble-own-muted)'
                : 'color-mix(in srgb, var(--chat-bubble-other) 50%, transparent)',
            }}
          >
            <p className="text-sm text-disabled italic">삭제된 메시지입니다</p>
          </div>
          {showTime && (
            <div className={clsx('flex items-center gap-1 mt-0.5 px-1', isOwn ? 'flex-row-reverse' : 'flex-row')}>
              <span className="text-meta text-disabled">{formatTime(message.createdAt)}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  // 롱프레스 핸들러
  const handlePointerDown = () => {
    if (!onLongPress) return
    longPressTimer.current = setTimeout(() => onLongPress(message.id), 400)
  }
  const handlePointerUp = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }

  // 스와이프 → 답장 (카카오톡 스타일)
  const swipeRef = useRef<{ startX: number; startY: number; swiped: boolean } | null>(null)
  const [swipeOffset, setSwipeOffset] = useState(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    swipeRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, swiped: false }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeRef.current) return
    const dx = e.touches[0].clientX - swipeRef.current.startX
    const dy = e.touches[0].clientY - swipeRef.current.startY

    // 수직 스크롤이면 무시
    if (Math.abs(dy) > Math.abs(dx)) { swipeRef.current = null; setSwipeOffset(0); return }

    // 답장 방향 (보낸 메시지: 왼쪽, 받은 메시지: 오른쪽)
    const direction = isOwn ? -1 : 1
    const offset = dx * direction > 0 ? dx : 0
    const clamped = Math.min(Math.abs(offset), 80)

    setSwipeOffset(clamped * direction)

    if (clamped >= 60) {
      swipeRef.current.swiped = true
      // 햅틱 피드백 (지원 시)
      if (navigator.vibrate) navigator.vibrate(10)
    }

    // 롱프레스 취소
    if (Math.abs(dx) > 10 && longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handleTouchEnd = () => {
    if (swipeRef.current?.swiped) {
      onReply(message.id)
    }
    swipeRef.current = null
    setSwipeOffset(0)
  }

  // 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current)
    }
  }, [])

  return (
    <div
      data-message-id={message.id}
      className={clsx('flex items-end gap-2 group relative', isOwn && 'flex-row-reverse')}
      style={swipeOffset !== 0 ? { transform: `translateX(${swipeOffset}px)`, transition: swipeRef.current ? 'none' : 'transform 0.2s ease-out' } : undefined}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={(e) => { if (onLongPress) e.preventDefault() }}
    >
      {/* 스와이프 답장 아이콘 */}
      {Math.abs(swipeOffset) > 20 && (
        <div
          className={clsx(
            'absolute top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center transition-opacity',
            Math.abs(swipeOffset) >= 60 ? 'opacity-100' : 'opacity-50'
          )}
          style={{ [isOwn ? 'left' : 'right']: '4px' }}
        >
          <Reply className={clsx('w-4 h-4', Math.abs(swipeOffset) >= 60 ? 'text-primary-500' : 'text-caption')} />
        </div>
      )}

      {/* Avatar (others only, 그룹핑 시 빈 공간 유지) */}
      {!isOwn && (
        isGrouped ? (
          <div className="w-7 flex-shrink-0" />
        ) : message.userPhotoURL ? (
          <img
            src={message.userPhotoURL}
            alt={message.userName}
            className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full flex-shrink-0 bg-surface-tertiary flex items-center justify-center">
            <span className="text-xs font-medium text-caption">
              {message.userName.charAt(0)}
            </span>
          </div>
        )
      )}

      <div className={clsx('max-w-[75%] flex flex-col', isOwn ? 'items-end' : 'items-start')}>
        {/* Bubble */}
        <div
          className={clsx(
            'rounded-xl relative',
            // 골드 배경(own + 일반 메시지)일 때만 다크 텍스트를 inline으로 강제하고,
            // 그 외(other 또는 배경 없는 이모지 전용)는 테마 적응형 text-heading 사용
            !(isOwn && !isEmojiOnly) && 'text-heading',
            isEmojiOnly
              ? 'px-1 py-0.5'
              : 'px-3 py-2',
            !isEmojiOnly && (isOwn ? 'rounded-br-sm' : 'rounded-bl-sm')
          )}
          style={
            !isEmojiOnly
              ? {
                  background: isOwn ? 'var(--chat-bubble-own)' : 'var(--chat-bubble-other)',
                  ...(isOwn ? { color: 'var(--chat-bubble-own-text)' } : null),
                }
              : undefined
          }
        >
          {/* Sender name (others only, 그룹핑 시 숨김) */}
          {!isOwn && !isGrouped && (
            <p className="text-label font-medium text-caption mb-0.5">
              {message.userName}
            </p>
          )}

          {/* Reply preview */}
          {message.replyTo && (
            <ChatReplyPreview
              userName={message.replyTo.userName}
              text={message.replyTo.text}
              imageUrl={message.replyTo.imageUrl}
              onNavigate={message.replyTo.messageId && onScrollToMessage
                ? () => onScrollToMessage(message.replyTo!.messageId)
                : undefined}
            />
          )}

          {/* Message content by type */}
          {messageType === 'text' && isEmojiOnly ? (
            <p className={clsx(
              'whitespace-pre-wrap break-words leading-tight',
              emojiCount <= 3 ? 'text-4xl' : 'text-3xl'
            )}>
              {message.text}
            </p>
          ) : messageType === 'text' && (
            <p className="text-sm whitespace-pre-wrap break-words">
              {renderTextWithLinks(message.text, message.mentions, members)}
            </p>
          )}

          {/* 링크 프리뷰 카드 */}
          {message.linkPreview && (message.linkPreview.title || message.linkPreview.image) && (
            <a
              href={message.linkPreview.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2 rounded-lg border border-base overflow-hidden bg-surface-secondary hover:bg-surface-tertiary transition-colors no-underline"
            >
              {message.linkPreview.image && (
                <img
                  src={message.linkPreview.image}
                  alt=""
                  className="w-full h-32 object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              )}
              <div className="px-3 py-2">
                {message.linkPreview.title && (
                  <p className="text-label font-semibold text-heading line-clamp-2">{message.linkPreview.title}</p>
                )}
                {message.linkPreview.description && (
                  <p className="text-meta text-caption line-clamp-2 mt-0.5">{message.linkPreview.description}</p>
                )}
                <p className="text-meta text-disabled mt-1 truncate">
                  {message.linkPreview.favicon && (
                    <img src={message.linkPreview.favicon} alt="" className="w-3 h-3 inline mr-1 align-middle" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                  )}
                  {new URL(message.linkPreview.url).hostname}
                </p>
              </div>
            </a>
          )}

          {messageType === 'image' && message.imageUrl && (
            <ChatImageMessage
              imageUrl={message.imageUrl}
              imageWidth={message.imageWidth}
              imageHeight={message.imageHeight}
              imageName={message.imageName}
            />
          )}

          {messageType === 'task_card' && message.taskSnapshot && (
            <ChatTaskCard
              snapshot={message.taskSnapshot}
              taskRef={message.taskRef}
              groupId={groupId}
              onToggle={onToggleTask}
            />
          )}

          {messageType === 'memo_card' && message.memoSnapshot && (
            <ChatMemoCard snapshot={message.memoSnapshot} />
          )}

          {messageType === 'voice' && message.voiceUrl && (
            <VoicePlayer url={message.voiceUrl} duration={message.voiceDuration || 0} />
          )}

          {messageType === 'file' && message.fileUrl && (
            <a
              href={message.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-base bg-surface-tertiary hover:bg-surface-primary transition-colors"
            >
              <FileText className="w-5 h-5 text-primary-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-meta font-medium text-heading truncate">{message.fileName || '파일'}</p>
                {message.fileSize && (
                  <p className="text-meta text-caption">{formatFileSize(message.fileSize)}</p>
                )}
              </div>
              <Download className="w-4 h-4 text-caption flex-shrink-0" />
            </a>
          )}

          {messageType === 'video' && message.fileUrl && (
            <div className="max-w-60 rounded-lg overflow-hidden">
              <video
                src={message.fileUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-lg"
                style={{ maxHeight: '300px' }}
              />
              {message.fileName && (
                <p className="text-meta text-caption mt-1 truncate">{message.fileName}</p>
              )}
            </div>
          )}

          {/* Hover actions (PC only) */}
          <div
            className={clsx(
              'absolute top-1 items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex',
              isOwn ? '-left-16' : '-right-16'
            )}
          >
            <button
              onClick={() => onReply(message.id)}
              className="p-1 rounded text-caption hover:text-primary-500 hover:bg-surface-tertiary transition-colors"
              title="답장"
            >
              <Reply className="w-4 h-4" />
            </button>
            {isOwn && !message.isDeleted && (
              <button
                onClick={() => onDelete(message.id)}
                className="p-1 rounded text-caption hover:text-red-500 hover:bg-surface-tertiary transition-colors"
                title="삭제"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Timestamp + unread count row (showTime=false 시 숨김) */}
        {showTime && (
          <div
            className={clsx(
              'flex items-center gap-1 mt-0.5 px-1',
              isOwn ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            {isOwn && unreadCount !== undefined && unreadCount > 0 && (
              <span className="text-meta font-medium text-amber-500">
                {unreadCount}
              </span>
            )}
            <span className="text-meta text-disabled">
              {formatTime(message.createdAt)}
            </span>
          </div>
        )}

        {/* Reactions row */}
        {hasReactions && (
          <div className="mt-1">
            <ReactionBar
              reactions={reactions}
              onAdd={(emoji) => onReactionAdd(message.id, emoji)}
              onRemove={(emoji) => onReactionRemove(message.id, emoji)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// React.memo로 감싸 입력창 키 입력 등 무관한 부모 리렌더 시 버블 재렌더를 방지한다.
// (콜백 props는 ChatPanel에서 메시지 id 기반 stable useCallback으로 전달됨)
export const ChatMessageBubble = memo(ChatMessageBubbleImpl)

// ─── 음성 플레이어 ───
function VoicePlayer({ url, duration }: { url: string; duration: number }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // 언마운트 시 오디오 정리
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.ontimeupdate = null
        audioRef.current.onended = null
        audioRef.current.src = ''
        audioRef.current = null
      }
    }
  }, [])

  const togglePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(url)
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100 || 0)
      }
      audioRef.current.onended = () => { setIsPlaying(false); setProgress(0) }
    }
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <button onClick={togglePlay} className="flex-shrink-0">
        {isPlaying ? <Pause className="w-5 h-5 text-primary-500" /> : <Play className="w-5 h-5 text-primary-500" />}
      </button>
      <div className="flex-1 h-1 bg-surface-tertiary rounded-full overflow-hidden">
        <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>
      <span className="text-meta text-caption font-mono flex-shrink-0">{formatDuration(duration)}</span>
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}
